// ** React Imports
import { useEffect, useState } from 'react';

// ** CoreUI Imports
import { CBadge, CCard, CCardBody, CLink, CModal, CModalBody } from '@coreui/react';
import CIcon from '@coreui/icons-react';

// ** Third Party Imports
import Tippy from '@tippyjs/react';
import { cilLightbulb } from '@coreui/icons';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from '../../../../../general/dropdown';
import { toast } from 'react-toastify';
import { cloneDeep } from 'lodash';

// ** Redux Imports
import { useDispatch, useSelector } from 'react-redux';

// ** Router Imports
import { useLocation } from 'react-router';

// ** Constants Imports
import { API_INTELLIGENCE_LLM_MODELS, CLIENT_REPORT_VIEW, DEFAULT_DATE_FORMAT } from '../../../../../../constants';
import { SAVED_VIEW_TYPE, VIEW_TYPE } from '../../../../../../constants/reportView';

// ** Utils Imports
import { callCompressTokenApi, callTokenApi } from '../../../../../../apiCaller';
import { decodeObj } from '../../../../../../utils';

// ** Actions Imports
import { fetchSeverSideTriggerRequest, setSavedReport } from '../../../../../../actions/subscriber';

// ** Components Imports
import CenterSpinner from '../../../../../general/Loadings/CenterSpinner';
import Confirm from './Confirm';
import ShareURL from './ShareURL';
import CreateNewView from './CreateNewView';
import VariableInRules from '../../../../../general/popup/VariableInRules';
import { ScheduleContext } from '../../saved-reports';
import ManageSchedule from '../../saved-reports/scheduled-deliveries/manageSchedule';
import ManageTemplate from '../../saved-reports/ai-insights/manageTemplate';

const ViewTemplateCard = ({ key, viewReport, handleConfirm, handleSchedule, handleAiTemplate }) => {
     const user = useSelector((state) => state.theme.user);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const intelligenceSetting = useSelector((state) => state.subscriber.intelligenceSetting);
     const itemDropdown = [
          {
               title: 'Delete',
               confirmTitle: 'Delete this Report',
               confirmDescription: 'You are about to delete this Report. Please confirm.',
               type: SAVED_VIEW_TYPE.DELETE,
               show: false,
          },
          {
               title: 'Settings',
               type: SAVED_VIEW_TYPE.SETTING,
               show: true,
          },
          {
               title: 'Share URL',
               confirmTitle: 'Share URL',
               confirmDescription: 'You can bookmark or share the URL below to quickly access this report.',
               type: SAVED_VIEW_TYPE.SHARE_URL,
               show: true,
          },
     ];
     const checkIsDelete = () => {
          if (activeAccount.isOwner) {
               return true;
          }

          const isDifferentUser = viewReport.userId !== user.id;
          return !isDifferentUser;
     };
     return (
          <div key={key} className="row-rule d-flex justify-content-between">
               <div className="info-wrap d-flex justify-content-between align-items-center">
                    <div className="info-title">
                         <h4>{viewReport.name}</h4>
                         {viewReport.description && (
                              <div className="d-inline-flex tooltip-schedule-card">
                                   <Tippy
                                        content={
                                             <div className="block-tooltip-text" style={{ fontSize: 10 }}>
                                                  <span>{viewReport.description}</span>
                                             </div>
                                        }
                                   >
                                        <CIcon icon="icon-info" height={14} />
                                   </Tippy>
                              </div>
                         )}
                    </div>
               </div>
               <div className="options-wrapper d-flex">
                    {checkIsDelete() && (
                         <Tippy content={<span style={{ fontSize: 10 }}>Delete Report</span>} placement="top">
                              <div className="option-item hover-show-tooltip">
                                   <CLink className="option option-item-delete" onClick={() => handleConfirm(itemDropdown[0], viewReport)}>
                                        <CIcon icon="iconDeleteField" className="icon-delete pointer" />
                                   </CLink>
                              </div>
                         </Tippy>
                    )}

                    {intelligenceSetting.aiInsightsEnabled && (
                         <Tippy content={<span style={{ fontSize: 10 }}>AI Insights</span>} placement="top">
                              <div className="option-item hover-show-tooltip save-report-bell notification-bell mr-0">
                                   <CLink className="option option-item-delete" onClick={() => handleAiTemplate(viewReport)}>
                                        <CIcon icon={cilLightbulb} />
                                        {viewReport.totalAiInsights > 0 && (
                                             <CBadge shape="pill" color="danger" className={viewReport.totalAiInsights > 99 ? 'big-number' : ''}>
                                                  {viewReport.totalAiInsights}
                                             </CBadge>
                                        )}
                                   </CLink>
                              </div>
                         </Tippy>
                    )}

                    <Tippy content={<span style={{ fontSize: 10 }}>View Report</span>} placement="top">
                         <div className="option-item hover-show-tooltip">
                              <CLink className="option option-item-delete" href={viewReport.shareLink} target="_blank">
                                   <CIcon icon="icon-eye" />
                              </CLink>
                         </div>
                    </Tippy>

                    <Tippy content={<span style={{ fontSize: 10 }}>Delivery Schedule</span>} placement="top">
                    <div className="option-item hover-show-tooltip save-report-bell notification-bell mr-0">
                         <CLink className="option option-item-delete" onClick={() => handleSchedule(viewReport)}>
                              <CIcon icon="cilEnvelopeClosed" />
                              {viewReport.totalSchedule > 0 && (
                                   <CBadge shape="pill" color="danger" className={viewReport.totalSchedule > 99 ? 'big-number' : ''}>
                                        {viewReport.totalSchedule}
                                   </CBadge>
                              )}
                         </CLink>
                         </div>
                    </Tippy>

                    <Tippy content={<span style={{ fontSize: 10 }}>Action</span>} placement="top">
                         <div className="option-item hover-show-tooltip">
                              <Dropdown>
                                   <DropdownToggle caret={false} tag="div" className="d-flex h-100">
                                        <CLink className="option option-item-delete">
                                             <CIcon icon="iconEllipsisVerticalCustom" height={18} />
                                        </CLink>
                                   </DropdownToggle>
                                   <DropdownMenu placement="right-start" container="body">
                                        {itemDropdown
                                             .filter((_item) => _item.show)
                                             .filter((_item) => {
                                                  if (activeAccount.isOwner) {
                                                       return true;
                                                  }
                                                  const isTypeFiltered = [SAVED_VIEW_TYPE.SETTING].includes(_item.type);

                                                  const isDifferentUser = viewReport.userId !== user.id;
                                                  return !(isTypeFiltered && isDifferentUser);
                                             })
                                             .map((_item, key) => (
                                                  <DropdownItem key={key} className="d-block" onClick={() => handleConfirm(_item, viewReport)}>
                                                       <div className="d-flex align-items-center mr-5" style={{ gap: '8px' }}>
                                                            <span>{_item.title}</span>
                                                       </div>
                                                  </DropdownItem>
                                             ))}
                                   </DropdownMenu>
                              </Dropdown>
                         </div>
                    </Tippy>
               </div>
          </div>
     );
};

const SaveReportInView = ({ showSaveReport, setShowSaveReport,  onCancel }) => {
     const dispatch = useDispatch();
     const user = useSelector((state) => state.theme.user);
     const activeAccount = useSelector((state) => state.subscriber.activeAccount);
     const view = showSaveReport;
     const savedReports = useSelector((state) => state.subscriber.savedReport);
     const location = useLocation();

     const scheduleParam = new URLSearchParams(location.search).get('scheduleSaveReport');

     const parseParams = decodeObj(scheduleParam);
     const scheduleReportId = parseParams?.id;
     const reportViewId = parseParams?.reportViewId;

     const [show, setShow] = useState(false);
     const [data, setData] = useState({});
     const [viewSelect, setViewSelect] = useState({});
     const [showSchedule, setShowSchedule] = useState(false);
     const [showAiTemplate, setShowAiTemplate] = useState(false);
     const [reportData, setReportData] = useState(null);
     const [variableInRules, setVariableInRules] = useState(false);
     const [models, setModels] = useState([]);
     const [isLoading, setIsLoading] = useState(false);
     const [existTrigger, setExistTrigger] = useState(false);     //sever side triggers
     const listSeverSideTrigger = useSelector((state) => state.subscriber.listSeverSideTrigger);

     const fetchSeverSideTrigger = () => {
          if (!listSeverSideTrigger || listSeverSideTrigger.length === 0) {
               dispatch(fetchSeverSideTriggerRequest(activeAccount.id));
          }
     };
     useEffect(fetchSeverSideTrigger, [activeAccount.id, listSeverSideTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

     const listSaveReportInView = savedReports.filter((report) => report.viewId === view.id);

     const handleConfirm = (_item, view) => {
          setShow(true);
          setData(_item);
          setViewSelect(view);
     };

     const handleSchedule = (item) => {
          setShowSchedule(true);
          setReportData(item);
     };

     const handleAiTemplate = (item) => {
          setShowAiTemplate(true);
          setReportData(item);
     };

     useEffect(() => {
          if (reportViewId && scheduleReportId) {
               const reportViewLink = savedReports.find((report) => report.id === reportViewId);
               handleSchedule(reportViewLink);
          }
     }, [parseParams]); // eslint-disable-line

     useEffect(() => {
          const loadData = async () => {
               setIsLoading(true);
               try {
                    // Load models data
                    const modelsResponse = await callTokenApi(API_INTELLIGENCE_LLM_MODELS, 'GET');
                    const modelsData = modelsResponse?.data?.data;
                    if (modelsResponse?.status === 200 && modelsData) {
                         setModels(modelsData);
                    } else {
                         toast.error('Failed to load models data.');
                    }
               } catch (error) {
                    toast.error('An error occurred while loading data.');
               } finally {
                    setIsLoading(false);
               }
          };

          loadData();
     }, []);
     const handleApiResponse = (response, type, savedReportId, successCallback) => {
          if (response.status === 200) {
               successCallback();

               let data = {};
               if (type === SAVED_VIEW_TYPE.DELETE) {
                    data = { id: savedReportId, deleted: true };
                    const savedReportsInView = savedReports.filter((report) => report.viewId === savedReportId).length;
                    if (savedReportsInView === 0) {
                         setShowSaveReport(false);
                    }
               } else {
                    data = response.data?.reportViews;
               }
               dispatch(setSavedReport(data));
               toast.success(response.data.message);
          } else {
               if (response?.data?.isUsed) {
                    setVariableInRules(true);
                    return;
               }

               toast.error(response.data.message);
          }
     };

     const handleApiError = () => {
          setShow(false);
     };

     const onAccept = async (values, { setSubmitting }) => {
          if (viewSelect.id) {
               let apiCall;
               let payload;
               if(data?.type === SAVED_VIEW_TYPE.DELETE && listSeverSideTrigger.length > 0 && listSeverSideTrigger.some((trigger) => trigger?.schedule?.reportViewId === viewSelect.id)) {
                    setExistTrigger(true);
                    setShow(false)
                    return;
               }  

               switch (data.type) {
                    case SAVED_VIEW_TYPE.DELETE: {
                         const query = `?id=${viewSelect.id}&accountId=${activeAccount.accountId}&userId=${user.id}&reportName=${viewSelect.reportName}`;
                         apiCall = callTokenApi(`${CLIENT_REPORT_VIEW}/${query}`, 'DELETE');
                         payload = null;
                         break;
                    }

                    case SAVED_VIEW_TYPE.SETTING: {
                         const dataDateRangeConfig = values.dateRangeConfig;
                         if (dataDateRangeConfig?.dateRanges) {
                              if (dataDateRangeConfig.dateRanges.selection) {
                                   dataDateRangeConfig.dateRanges.selection = {
                                        startDate: dataDateRangeConfig.dateRanges.selection.startDate.format(DEFAULT_DATE_FORMAT),
                                        endDate: dataDateRangeConfig.dateRanges.selection.endDate.format(DEFAULT_DATE_FORMAT),
                                   };
                              }

                              if (dataDateRangeConfig.dateRanges.compare) {
                                   dataDateRangeConfig.dateRanges.compare = {
                                        startDate: dataDateRangeConfig.dateRanges.compare.startDate.format(DEFAULT_DATE_FORMAT),
                                        endDate: dataDateRangeConfig.dateRanges.compare.endDate.format(DEFAULT_DATE_FORMAT),
                                   };
                              }
                         }
                         if (dataDateRangeConfig?.rolling?.customDateCompare) {
                              if (dataDateRangeConfig.rolling.customDateCompare.selection) {
                                   dataDateRangeConfig.rolling.customDateCompare.selection = {
                                        startDate: dataDateRangeConfig.rolling.customDateCompare.selection.startDate.format(DEFAULT_DATE_FORMAT),
                                        endDate: dataDateRangeConfig.rolling.customDateCompare.selection.endDate.format(DEFAULT_DATE_FORMAT),
                                   };
                              }
                         }

                         const content = cloneDeep(viewSelect.content);
                         content.dateRangeConfig = dataDateRangeConfig;

                         payload = {
                              id: viewSelect.id,
                              accountId: activeAccount.accountId,
                              name: values.name,
                              description: values.description,
                              isAvailable: values.isAvailable,
                              content: JSON.stringify(content),
                              isReport: true,
                         };

                         apiCall = callCompressTokenApi(CLIENT_REPORT_VIEW, 'PUT', payload);

                         break;
                    }

                    default:
                         return;
               }
               try {
                    const response = await apiCall;

                    handleApiResponse(response, data.type, viewSelect.id, () => {});
               } catch (error) {
                    toast.error('An error occurred while processing your request.');
               } finally {
                    setSubmitting(false);
                    handleApiError();
               }
          }
     };

     const contextValue = {
          showSchedule,
          setShowSchedule,
          showAiTemplate,
          setShowAiTemplate,
          reportData,
          setReportData,
          scheduleReportId,
          models,
     };

     return (
          <div className="savedReport-in-view">
               <div className="savedReport-in-view-modal form-categories schedule-saved-report goals">
                    <CModal
                         className="schedule-modal"
                         visible={showSaveReport}
                         alignment="center"
                         onClose={onCancel}
                         color="primary"
                         portal={false}
                         backdrop="static"
                    >
                         <CModalBody className="goalsContent">
                              <CCard className="card form-categories--card form-categories--categorizing">
                                   <CIcon icon="cil-x" onClick={onCancel} className="icon-close-popup" />
                                   <CCardBody>
                                        <div className="title">
                                             <h3>Reports Using this View</h3>
                                             <p>{`The ${view.name} ${view.isSystem ? '': 'View'} is being used in the following saved reports.`}</p>
                                        </div>
                                        <div className="form-actions text-center">
                                             <div className={`list-rules ${!isLoading ? 'list-rules-data' : ''}`}>
                                                  {isLoading ? (
                                                       <CenterSpinner />
                                                  ) : (
                                                       listSaveReportInView.map((viewReport, index) => (
                                                            <ViewTemplateCard
                                                                 key={viewReport.id || index}
                                                                 index={index}
                                                                 viewReport={viewReport}
                                                                 handleConfirm={handleConfirm}
                                                                 handleSchedule={handleSchedule}
                                                                 handleAiTemplate={handleAiTemplate}
                                                            />
                                                       ))
                                                  )}
                                             </div>
                                        </div>
                                   </CCardBody>
                              </CCard>
                         </CModalBody>
                    </CModal>
               </div>
               {(data.type === SAVED_VIEW_TYPE.SET_DEFAULT || data.type === SAVED_VIEW_TYPE.DELETE) && (
                    <Confirm show={show} data={data} onCancel={() => setShow(false)} onAccept={onAccept} />
               )}
               {data.type === SAVED_VIEW_TYPE.SETTING && (
                    <CreateNewView show={show} type={VIEW_TYPE.REPORT} data={viewSelect} onAccept={onAccept} onCancel={() => setShow(false)} />
               )}
               {data.type === SAVED_VIEW_TYPE.SHARE_URL && (
                    <ShareURL link={viewSelect.shareLink} show={show} data={data} onCancel={() => setShow(false)} />
               )}
               {reportData && (
                    <ScheduleContext.Provider value={contextValue}>
                         <ManageSchedule savedReports={savedReports} />
                         <ManageTemplate />
                    </ScheduleContext.Provider>
               )}
               <VariableInRules show={variableInRules} onClose={() => setVariableInRules(false)}>
                    {data?.type === SAVED_VIEW_TYPE.SETTING ? (
                         <p>
                              This report is used in the AI Insight. You must remove it from all existing template before you can change date range
                              option for it.
                         </p>
                    ) : (
                         <p>This report is used in the AI Insight. You must remove it from all existing template before you can delete it.</p>
                    )}
               </VariableInRules>
               <VariableInRules show={existTrigger} onClose={() => setExistTrigger(false)}>
                    <p>This report is used in Server Side Trigger. You must remove it from all existing template before you can delete it.</p>
               </VariableInRules>
          </div>
     );
};

export default SaveReportInView;
